// What the user chose about helping the catalogue grow, kept in localStorage so it survives a
// reload: whether to be asked to scan the barcode of a picked wine that has none on file (About >
// Help the catalogue; on by default, because the prompt is dismissible), and the wines this device
// already submitted a barcode for, so it never asks for the same one twice.
import { useSyncExternalStore } from "react";

/**
 * A barcode this device submitted for a wine: `pending` while the submission waits in the outbox or
 * for review, `accepted` once the edge confirmed it. Either way the wine is not asked about again.
 */
export interface SubmittedBarcode {
  readonly gtin: string;
  readonly status: "pending" | "accepted";
  /** Epoch ms of the submission, to drop the oldest first when the list is full. */
  readonly at: number;
}

export interface ContributePrefs {
  readonly askBarcode: boolean;
  /** By suggestion key (stable per wine, catalogue or local). */
  readonly submittedBarcodes: Readonly<Record<string, SubmittedBarcode>>;
}

const STORAGE_KEY = "proef.contribute";
const MAX_SUBMITTED = 500;
const DEFAULTS: ContributePrefs = { askBarcode: true, submittedBarcodes: {} };

function parseSubmitted(value: unknown): Record<string, SubmittedBarcode> {
  const out: Record<string, SubmittedBarcode> = {};
  if (typeof value !== "object" || value === null) return out;
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry !== "object" || entry === null) continue;
    const gtin = "gtin" in entry ? entry.gtin : undefined;
    const status = "status" in entry ? entry.status : undefined;
    const at = "at" in entry ? entry.at : undefined;
    if (typeof gtin !== "string" || typeof at !== "number") continue;
    if (status !== "pending" && status !== "accepted") continue;
    out[key] = { gtin, status, at };
  }
  return out;
}

function parse(raw: string | null): ContributePrefs {
  if (raw === null) return DEFAULTS;
  try {
    const stored: unknown = JSON.parse(raw);
    if (typeof stored !== "object" || stored === null) return DEFAULTS;
    const askBarcode = "askBarcode" in stored ? stored.askBarcode : undefined;
    return {
      askBarcode: typeof askBarcode === "boolean" ? askBarcode : DEFAULTS.askBarcode,
      submittedBarcodes: parseSubmitted(
        "submittedBarcodes" in stored ? stored.submittedBarcodes : undefined,
      ),
    };
  } catch {
    return DEFAULTS;
  }
}

function read(): ContributePrefs {
  try {
    return parse(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    // Storage blocked (private mode): the defaults.
    return DEFAULTS;
  }
}

let current: ContributePrefs = typeof window === "undefined" ? DEFAULTS : read();
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((l) => {
    l();
  });
}

export function getContributePrefs(): ContributePrefs {
  return current;
}

export function setAskBarcode(askBarcode: boolean): void {
  commit({ ...current, askBarcode });
}

function commit(next: ContributePrefs): void {
  current = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    // Not persisting is fine: the choice lasts this session.
  }
  notify();
}

/** The barcode this device already submitted for the wine, if any. */
export function submittedBarcodeFor(wineKey: string): SubmittedBarcode | undefined {
  return current.submittedBarcodes[wineKey];
}

/** Remembers a barcode submitted for the wine, so it is not asked about again. */
export function rememberSubmittedBarcode(wineKey: string, gtin: string, now = Date.now()): void {
  const kept = Object.entries(current.submittedBarcodes)
    .filter(([key]) => key !== wineKey)
    .toSorted(([, a], [, b]) => a.at - b.at)
    .slice(-(MAX_SUBMITTED - 1));
  commit({
    ...current,
    submittedBarcodes: {
      ...Object.fromEntries(kept),
      [wineKey]: { gtin, status: "pending", at: now },
    },
  });
}

/** The edge confirmed the submission of this barcode: every wine waiting on it is accepted. */
export function markBarcodeAccepted(gtin: string): void {
  const entries = Object.entries(current.submittedBarcodes);
  if (!entries.some(([, v]) => v.gtin === gtin && v.status === "pending")) return;
  commit({
    ...current,
    submittedBarcodes: Object.fromEntries(
      entries.map(([key, v]) => [key, v.gtin === gtin ? { ...v, status: "accepted" as const } : v]),
    ),
  });
}

/** Forgets what was submitted for the wine, so it may be asked about again. */
export function forgetSubmittedBarcode(wineKey: string): void {
  if (!(wineKey in current.submittedBarcodes)) return;
  commit({
    ...current,
    submittedBarcodes: Object.fromEntries(
      Object.entries(current.submittedBarcodes).filter(([key]) => key !== wineKey),
    ),
  });
}

/** Forgets every wine that waited on this barcode (the edge refused it, or it went stale). */
export function forgetSubmittedGtin(gtin: string): void {
  const entries = Object.entries(current.submittedBarcodes);
  if (!entries.some(([, v]) => v.gtin === gtin)) return;
  commit({
    ...current,
    submittedBarcodes: Object.fromEntries(entries.filter(([, v]) => v.gtin !== gtin)),
  });
}

export function subscribeContributePrefs(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useContributePrefs(): ContributePrefs {
  return useSyncExternalStore(subscribeContributePrefs, getContributePrefs, () => DEFAULTS);
}

/** Test seam: re-read storage as a fresh page load would. */
export function reloadContributePrefs(): void {
  current = read();
  notify();
}

/** Test seam: forget the stored choice and go back to the defaults. */
export function resetContributePrefs(): void {
  current = DEFAULTS;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored to forget.
  }
  notify();
}
